import React, { useState } from 'react';
import { Users, AlertTriangle, Archive, Loader2, PlayCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

export const LimitsTab = ({ settings, handleChange }) => {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const autoArchiveDays = Number(settings.auto_archive_awaiting_info_days ?? 60);

  const runAutoArchive = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/super-admin/run-auto-archive-awaiting-info');
      setLastRun(data);
      if (data.disabled) {
        toast.info('Auto-arquivo desactivado (limite = 0). Configura um valor > 0 para activar.');
      } else if (data.archived === 0) {
        toast.success('Job executado — nenhuma denúncia elegível para arquivo.');
      } else {
        toast.success(`Job executado — ${data.archived} denúncia(s) arquivada(s).`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao executar auto-arquivo');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-indigo-700" />
            Limites por Defeito
          </CardTitle>
          <CardDescription className="text-slate-500">
            Limites aplicados a novos clientes (pode ser alterado por cliente)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600">Máx. Utilizadores por Cliente</Label>
              <Input
                type="number"
                min="1"
                value={settings.default_max_users}
                onChange={(e) => handleChange('default_max_users', parseInt(e.target.value) || 5)}
                className="bg-white border-slate-300 text-slate-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">Máx. Denúncias/Mês por Cliente</Label>
              <Input
                type="number"
                min="1"
                value={settings.default_max_reports_month}
                onChange={(e) => handleChange('default_max_reports_month', parseInt(e.target.value) || 50)}
                className="bg-white border-slate-300 text-slate-900"
              />
            </div>
          </div>

          <Alert className="bg-amber-900/20 border-indigo-200">
            <AlertTriangle className="w-4 h-4 text-indigo-700" />
            <AlertDescription className="text-amber-800">
              Estes limites são aplicados a novos clientes. Clientes existentes mantêm os seus limites atuais.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Archive className="w-5 h-5 text-slate-700" />
            Auto-arquivo de denúncias sem resposta
          </CardTitle>
          <CardDescription className="text-slate-500">
            Arquiva denúncias em AGUARDA_INFO cuja última actividade seja mais antiga que o limite.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600">Limite (dias) — 0 desactiva</Label>
              <Input
                type="number"
                min="0"
                max="365"
                value={autoArchiveDays}
                onChange={(e) => handleChange('auto_archive_awaiting_info_days', Math.max(0, parseInt(e.target.value) || 0))}
                data-testid="auto-archive-days-input"
                className="bg-white border-slate-300 text-slate-900"
              />
              <p className="text-xs text-slate-500">
                O alerta ao gestor de denúncias aparece a partir de metade deste valor.
              </p>
            </div>
            <div className="flex items-end">
              <Button
                onClick={runAutoArchive}
                disabled={running || autoArchiveDays <= 0}
                variant="outline"
                className="w-full"
                data-testid="run-auto-archive-btn"
              >
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Executar agora
              </Button>
            </div>
          </div>

          {lastRun && (
            <Alert className="bg-slate-50 border-slate-200">
              <AlertDescription className="text-sm text-slate-700" data-testid="auto-archive-result">
                {lastRun.disabled ? (
                  <>Auto-arquivo <strong>desactivado</strong> (limite = 0).</>
                ) : lastRun.archived === 0 ? (
                  <>Sem denúncias elegíveis para arquivo (limite: {lastRun.threshold_days} dias).</>
                ) : (
                  <>
                    Arquivadas <strong>{lastRun.archived}</strong> denúncia(s) com actividade anterior a{' '}
                    {new Date(lastRun.cutoff).toLocaleDateString('pt-PT')}.
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          <Alert className="bg-blue-50 border-blue-200">
            <AlertTriangle className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-800 text-xs">
              Cada denúncia arquivada por este job recebe: mensagem padrão ao denunciante, email de notificação, assinatura SHA-256, e audit log <code className="bg-blue-100 px-1 rounded">REPORT_AUTO_ARCHIVED</code>. É sempre reversível via "Reabrir denúncia".
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

